import { AfterContentInit, Directive, Input, HostListener, inject } from '@angular/core';
import { PrimeNG } from 'primeng/config';
import * as _ from 'lodash';

import { HisDateUtils } from '../../core/utils';
import { HisBaseGlobalsService } from '../../core/service/base/his-base-globals.service';
import { HisUiService } from '../../core/service/base/his-ui.service';

import { Calendar } from 'primeng/calendar';

/**
 * Sets default values for p-calendar component bindings that are universally used in H1.
 *
 * Default values are as follows:
 * showIcon: true
 * monthNavigator: true
 * yearNavigator: true
 * yearRange: 1900:2100
 * styleClass: calendarWidth40
 * placeholder: common.dateFormatPlaceholder
 * dateFormat: depending on localization
 * locale: depending on localization
 */
@Directive( {
    // eslint-disable-next-line @angular-eslint/directive-selector
    selector: 'p-calendar',
    standalone: false
} )
export class HisCalendarDefaultsDirective implements AfterContentInit {
    hisBaseGlobalsService = inject(HisBaseGlobalsService);
    private hisUiService = inject(HisUiService);
    private config = inject(PrimeNG);
    private calendarRef = inject(Calendar);


    private static languageToLocalesMap = new Map<string, any>( [
        ['de', {
            firstDayOfWeek: 1,
            dayNames: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
            dayNamesShort: ['Son', 'Mon', 'Die', 'Mit', 'Don', 'Fre', 'Sam'],
            dayNamesMin: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
            monthNames: [
                'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
                'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
            monthNamesShort: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
            today: 'Heute',
            clear: 'Löschen'
        } ],
        ['en', {
            firstDayOfWeek: 0,
            dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
            dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
            dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
            monthNames: [
                'January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'],
            monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
            today: 'Today',
            clear: 'Clear'
        } ]
    ] );

    @Input() yearRange: string;

    public static getDateFormat( lang: string ): string {
        let dateFormat: string = HisDateUtils.languageToDateFormatsMap.get( lang );
        if ( dateFormat ) { return dateFormat; }
        console.log( 'HisCalendarDefaultsDirective.getDateFormat(): Language "' + lang + '" is not supported yet.'
            + 'Return dateFormat for default language "de".' );
        return HisDateUtils.languageToDateFormatsMap.get( 'de' );
    }

    public static getMonthYearFormat( lang: string ): string {
        let monthYearFormat: string = HisDateUtils.languageToMonthYearFormatsMap.get( lang );
        if ( monthYearFormat ) { return monthYearFormat; }
        console.log( 'HisCalendarDefaultsDirective.getMonthYearFormat(): Language "' + lang + '" is not supported yet.'
            + 'Return monthYearFormat for default language "de".' );
        return HisDateUtils.languageToMonthYearFormatsMap.get( 'de' );
    }

    ngAfterContentInit(): void {
        if ( this.calendarRef ) {
            if ( !this.calendarRef.showIcon ) {
                this.calendarRef.showIcon = true;
            }
            if ( !this.calendarRef.monthNavigator ) {
                this.calendarRef.monthNavigator = true;
            }
            if ( !this.calendarRef.yearNavigator ) {
                this.calendarRef.yearNavigator = true;
            }
            if ( this.yearRange ) {
                this.calendarRef.yearRange = this.yearRange;
            } else if ( !this.calendarRef.yearRange ) {
                this.calendarRef.yearRange = '1900:2100';
            }
            if ( !this.calendarRef.styleClass ) {
                this.calendarRef.styleClass = 'calendarWidth40';
            }
            if ( !this.calendarRef.placeholder ) {
                this.calendarRef.placeholder = this.hisUiService.translate( 'common.dateFormatPlaceholder' );
            }

            // the calendar should not open on focus to avoid problems with test automation #219913
            this.calendarRef.showOnFocus = false;

            let lang = this.hisBaseGlobalsService.getLanguage();
            if( this.calendarRef._view === 'year'){
                this.calendarRef.dateFormat = 'yy';
            } else if ( this.calendarRef._view === 'month' ) {
                this.calendarRef.dateFormat = HisCalendarDefaultsDirective.getMonthYearFormat( lang );
            } else {
                this.calendarRef.dateFormat = HisCalendarDefaultsDirective.getDateFormat( lang );
            }
            this.config.setTranslation( HisCalendarDefaultsDirective.languageToLocalesMap.get( lang ) );
            this.calendarRef.ngOnInit();
        }
        // console.log( this.calendarRef );
    }

    /**
     * host listener onInput implementation to clear model if input was null or empty
     *
     * @param event KeyboardEvent originally is InputEvent
     * -> which is an experimental interface (@types/dom-inputevent) and currently not present in package.json.
     * Using workaround via KeyboardEvent
     */
    @HostListener( 'onInput', ['$event'] )
    onInput( event: Event ) {
        const input = ( event.target as HTMLInputElement ).value;
        if ( _.isEmpty( input ) ) {
            this.calendarRef.updateModel( null );
        }
    }

    @HostListener('click', ['$event']) clickEvent(event: any) {
        let spanClassNames = ['p-button-icon', 'pi', 'pi-calendar'];
        if (spanClassNames.some(spanClassName => event.target.classList.contains(spanClassName)) || event.target.classList.contains('p-datepicker-trigger')) {
            if(this.calendarRef.overlayVisible && this.calendarRef.overlay){
                let offset = 25;
                let elementRect = this.calendarRef.overlay.getBoundingClientRect();
                if(elementRect.right > (window.innerWidth || document.documentElement.clientWidth)) {
                    let newPosition = this.calendarRef.overlay.getBoundingClientRect().right - window.innerWidth + offset;
                    this.calendarRef.overlay.style.left = '-' + newPosition.toString() + 'px';
                }
            }
        }
    }
}
